import React, { useState } from 'react';
import { Label, Table } from 'reactstrap';
import { TABLE_SORT_AESC } from '../utils/Constant';
import { Image, OverlayTrigger, Tooltip } from 'react-bootstrap';
import SortIcon from '../assets/img/sort.svg';
import {
  ColumnHeaderType,
  LocationFilterInitialState,
  SelectOptionType,
} from '../utils/Types';
import {
  add3Dots,
  canShowToolTip,
  getDefaultImage,
  getPhotoSrc,
  getTableItemClickClass,
} from '../utils/Util';
import { locationFilterSelector } from '../features/locationFilterSlice';
import { useAppSelector } from '../store/redux-hooks';
import { findSelectValue, findValue, handleSort } from '../utils/TableHelper';
import DoneIcon from '@mui/icons-material/Done';
import DotMenu from '../assets/img/dot_menu.svg';
import RedBG from '../assets/img/red_bg.svg';
import { trackLocationSelector } from '../features/trackLocationSlice';

interface IKTableProps {
  data: any[];
  columnHeader: any[];
  onAction: (param: any, header: ColumnHeaderType) => void;
  tableParentClass: string;
  noDataFoundMsg?: string | null;
  isLoading: boolean;
  highlightRow?: boolean;
  selectCustomStyle?: any;
  selectOnChange?: (event: any, param: any) => void;
  selectOptions?: any[];
  locationData?: LocationFilterInitialState;
  primaryEmpUniqueId?: string | number;
  showNoDataFoundMsg?: boolean;
  showHeader?: boolean;
}
const KTable: React.FunctionComponent<IKTableProps> = ({
  data,
  columnHeader,
  onAction,
  tableParentClass,
  noDataFoundMsg,
  isLoading,
  highlightRow,
  selectCustomStyle,
  selectOnChange,
  selectOptions,
  locationData,
  primaryEmpUniqueId,
  showNoDataFoundMsg = true,
  showHeader,
}: IKTableProps) => {
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<string>(TABLE_SORT_AESC);
  const localLocationData: LocationFilterInitialState = useAppSelector(
    locationFilterSelector
  );
  const roleOption: SelectOptionType[] = useAppSelector(
    trackLocationSelector
  ).roleOptions;

  const sortedData = sortBy
    ? [...data].sort((a, b) => {
        const itemA = a[sortBy as keyof any];
        const itemB = b[sortBy as keyof any];
        if (itemA < itemB) return sortOrder === TABLE_SORT_AESC ? -1 : 1;
        if (itemA > itemB) return sortOrder === TABLE_SORT_AESC ? 1 : -1;
        return 0;
      })
    : data;

  const getTableTd = (
    headerItem: ColumnHeaderType,
    item: any,
    headerIndex: number,
    index: any
  ) => {
    return (
      <div className="d-flex align-items-center">
        {headerItem.showProfilePhoto && (
          <div className="list-image">
            <Image
              roundedCircle
              width={25}
              height={25}
              src={getPhotoSrc(item.photo)}
              className="profile-image-icon me-2"
              onError={(e) => {
                e.currentTarget.src = getDefaultImage();
              }}
            />
          </div>
        )}
        {headerItem.toolTipAfter &&
          canShowToolTip(headerItem.toolTipAfter, item, headerItem.field) && (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id={`id${headerItem.field}_${headerIndex}_${index}`}>
                  {findValue(
                    headerItem,
                    item,
                    locationData ?? localLocationData,
                    roleOption
                  )}
                </Tooltip>
              }
            >
              <span>
                {add3Dots(
                  findValue(
                    headerItem,
                    item,
                    locationData ?? localLocationData,
                    roleOption
                  ),
                  headerItem.toolTipAfter
                )}
              </span>
            </OverlayTrigger>
          )}
        {headerItem.radio && (
          <input
            type="radio"
            value="option1"
            aria-label="radio"
            checked={item.employeeId === primaryEmpUniqueId}
          />
        )}
        {headerItem.showTickMark && item.isPrimary && (
          <DoneIcon className="tick-mark" />
        )}
        {headerItem.showTickMark && !item.isPrimary && '-'}
        {headerItem?.leftIcon?.(item, headerItem.field) && (
          <Image
            src={headerItem.leftIcon(item, headerItem.field)}
            className="pe-2"
            height={24}
            width={24}
          />
        )}
        {headerItem?.customComponent && (
          <>
            {headerItem?.customComponentName === 'EMPLOYEE-INFORMATION' && (
              <select
                name="employeeStatus"
                id="selectEmployeeStatus"
                className="custom-select-table "
                value={findSelectValue(headerItem, item, selectOptions)?.value}
                onChange={(event: any) => {
                  if (selectOnChange) selectOnChange(event, item);
                }}
              >
                {(selectOptions || []).map((item: SelectOptionType) => (
                  <option key={String(item.value)} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            )}
            {headerItem?.customComponentName === 'ROLE-MANAGEMENT' && (
              <div>
                <Image src={DotMenu} />
                <Image src={RedBG} className="role-count-bg" />
                <div className="role-count-span">
                  {headerItem?.valueGetter
                    ? headerItem?.valueGetter(item, headerItem.field)
                    : 0}
                </div>
              </div>
            )}
          </>
        )}

        {!headerItem.radio &&
          headerItem?.customComponentName !== 'ROLE-MANAGEMENT' &&
          headerItem?.customComponentName !== 'EMPLOYEE-INFORMATION' &&
          ((headerItem.toolTipAfter &&
            !canShowToolTip(headerItem.toolTipAfter, item, headerItem.field)) ||
            !headerItem.toolTipAfter) && (
            <span className="td-span w-100">
              {findValue(
                headerItem,
                item,
                locationData ?? localLocationData,
                roleOption
              )}
              {headerItem?.successIcon &&
                item.employeeId === primaryEmpUniqueId && (
                  <DoneIcon className="tick-mark-bg" />
                )}
            </span>
          )}
      </div>
    );
  };
  return (
    <>
      {((data && data.length > 0) || showHeader) && (
        <div className={`table-parent ${tableParentClass}`}>
          <Table striped hover>
            <thead>
              <tr>
                {columnHeader.map((column: any, index: number) => (
                  <th
                    key={`th-${column.field}_${index}`}
                    onClick={() =>
                      column.sortable &&
                      handleSort(
                        column.field,
                        sortBy,
                        sortOrder,
                        setSortOrder,
                        setSortBy
                      )
                    }
                  >
                    <div className="d-flex flex-row thead-text mb-1">
                      {column.name}
                      {column.sortable && (
                        <div className="ms-2 text-gray-400">
                          <Image src={SortIcon} />
                        </div>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedData.map((item: any, index: number) => (
                <tr
                  key={`table-tr-${String(index)}`}
                  className={
                    highlightRow && item?.topPerformer ? 'highlight-row' : ''
                  }
                >
                  {columnHeader.map(
                    (headerItem: ColumnHeaderType, headerIndex: number) => (
                      <td
                        key={`td${headerItem.field}_${headerIndex}`}
                        className={` ${getTableItemClickClass(item, headerItem)}
                          ${
                            headerItem.classNameGetter
                              ? headerItem.classNameGetter(
                                  item,
                                  headerItem.field
                                )
                              : ''
                          }`}
                        onClick={() => {
                          (headerItem.action ?? headerItem.actionGetter) &&
                            headerItem.clickable &&
                            onAction(item, headerItem);
                        }}
                      >
                        {getTableTd(headerItem, item, headerIndex, index)}
                      </td>
                    )
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
      {(!data || data.length == 0) && !isLoading && showNoDataFoundMsg && (
        <div className="d-flex justify-content-center data-not-found table-parent-onboarding">
          <Label className="data-not-found-label">
            {noDataFoundMsg ?? ' Data not found'}
          </Label>
        </div>
      )}
    </>
  );
};

export default KTable;
